import classNames from "classnames";
import React, { useContext, useState } from "react";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { t } from "ttag";

import {
    ContentHubCategoryMenuBlock as CategoryMenuBlockValue,
    Context,
    ContentHubLinkMenuBlock as LinkMenuBlockValue,
    templates,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

const isLinkActive = (
    linkUrl: string,
    currentPath?: string | null,
): boolean => {
    if (!currentPath) {
        return false;
    }
    // Use base URLs to ensure relative paths parse correctly
    const target = new URL(linkUrl, "http://example.com");
    const current = new URL(currentPath, "http://example.com");
    return target.pathname === current.pathname;
};

const MenuItem = (props: {
    displayName: string;
    linkURL: string;
    linkActive: boolean;
    iconURL: string | undefined;
}): React.ReactNode => {
    const navItemClasses = classNames({
        "menu__nav-item": true,
        "menu__nav-item--active": props.linkActive,
    });
    return (
        <li className={navItemClasses}>
            <Link
                href={isoWebPageURL.wrap(props.linkURL)}
                className="menu__nav-link"
            >
                <div className="menu__nav-link-left">
                    <div className="menu__nav-icon">
                        <img
                            className="menu__nav-image"
                            src={props.iconURL}
                            alt={props.displayName}
                        />
                    </div>
                    <span className="menu__nav-copy">{props.displayName}</span>
                </div>
                <span className="menu__arrow--right"></span>
            </Link>
        </li>
    );
};

const LinkMenuBlock = (props: {
    value: LinkMenuBlockValue;
}): React.ReactNode => {
    const context = useContext(Context);
    const linkURL = props.value.link?.url || "/";
    return (
        <MenuItem
            displayName={props.value.display_name || ""}
            linkURL={linkURL}
            linkActive={isLinkActive(linkURL, context.request.path)}
            iconURL={props.value.icon?.url}
        />
    );
};

const CategoryMenuBlock = (props: {
    value: CategoryMenuBlockValue;
    loveRootURL: string;
}): React.ReactNode => {
    const context = useContext(Context);
    const linkURL = `${props.loveRootURL}c/${props.value.category?.slug}/`;
    return (
        <MenuItem
            displayName={props.value.category?.name || ""}
            linkURL={linkURL}
            linkActive={isLinkActive(linkURL, context.request.path)}
            iconURL={props.value.category?.image.url}
        />
    );
};

// Composed as a React child of the live CustomerContentHubRootPage /
// ...Document page templates, which pass their own ``love_root`` (non-null
// under the document guards); derive the shape from the root template so
// the two stay in sync. Not Immutable<>: ``menu.value`` is spread into
// StreamField, which needs the mutable reactivated value.
export type ICustomerContentHubHeaderProps = {
    love_root: NonNullable<
        templates.CustomerContentHubRootPageTemplate["love_root"]
    >;
};

export const CustomerContentHubHeader = (
    props: ICustomerContentHubHeaderProps,
) => {
    const context = useContext(Context);
    const [isExpanded, setIsExpanded] = useState(false);
    const { url, header_content, header_background, menu } = props.love_root;

    const onClickToggleMenu = () => {
        setIsExpanded((expanded) => !expanded);
    };

    const menuListClasses = classNames({
        "menu__nav": true,
        "menu__nav--expanded": isExpanded,
        "menu__nav--collapsed": !isExpanded,
    });

    return (
        <main
            className="l-full-width tempurlove-hero"
            style={{
                background: `url(${header_background.block.url}) repeat-x`,
            }}
            data-tempur-love-root-url={url}
        >
            <div>
                <header className="tempurlove-hero__header">
                    <article className="tempurlove-hero__copy">
                        <a href={url ?? "/"} title="Sealy Living">
                            <img
                                src={`${context.STATIC_URL}img/logos/sealy-living-logo.png`}
                                className="img-responsive"
                                alt="Sealy Living"
                            />
                        </a>
                        <RichText html={header_content.html} />
                    </article>

                    <nav className="menu" aria-label="Main categories">
                        <button
                            type="button"
                            className="menu__button button--reset-button"
                            aria-expanded={isExpanded}
                            aria-controls="menu-list"
                            aria-label="Toggle categories menu"
                            onClick={onClickToggleMenu}
                        >
                            <span className="menu__open">{t`Show`}</span>
                            {` `}
                            <span className="menu__close">{t`Hide`}</span>
                            {` `}
                            {t`Categories`}
                            {` `}
                            <span className="menu__open menu__arrow menu__arrow--down"></span>
                            <span className="menu__close menu__arrow menu__arrow--up"></span>
                        </button>
                        <ul
                            id="menu-list"
                            className={menuListClasses}
                            aria-hidden={!isExpanded}
                        >
                            {menu && (
                                <StreamField
                                    tagName={React.Fragment}
                                    value={menu.value}
                                    components={{
                                        link_menu: LinkMenuBlock,
                                        category_menu: (props) => (
                                            <CategoryMenuBlock
                                                {...props}
                                                loveRootURL={url || "/"}
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </ul>
                    </nav>
                </header>
            </div>
        </main>
    );
};
